import { Avatar, Box, Button, Card, Group, Text, Title } from "@mantine/core";
import mantaClassicLogo from "@/assets/Manta_Classic_appicon.png";
import { COLORS } from "@/utils/GlobalStyles";
import CurrentPlanComponent from "./CurrentPlan";

interface PlanCardProps {
  title: string;
  subtitle: string;
  price: string;
  priceSubtitle?: string;
  features: string[];
  isCurrentPlan: boolean;
  hidePlan?: boolean;
  setOpenedModalCancel: (value: boolean) => void;
  isFreePlan?: boolean;
  lookupKey?: string;
  extraTexts?: () => React.ReactNode;
  disabled?: boolean;
  onSubscribe?: (lookupKey: string, force?: boolean, extraParams?: any) => void;
  renderButton?: () => React.ReactNode;
  buttonLoading?: boolean;
  canceled?: boolean;
}

const PlanCard = ({
  title,
  subtitle,
  price,
  priceSubtitle,
  features,
  isCurrentPlan,
  hidePlan,
  setOpenedModalCancel,
  isFreePlan,
  lookupKey,
  extraTexts,
  disabled,
  onSubscribe,
  renderButton,
  buttonLoading,
  canceled,
}: PlanCardProps) => {
  if (hidePlan) return null;

  const customButton = renderButton?.();

  const renderDefaultButton = () => {
    if (canceled) {
      return (
        <Button
          fw={500}
          h={45}
          disabled={isFreePlan || disabled || buttonLoading}
          loading={buttonLoading}
          onClick={() => onSubscribe?.(lookupKey || "", true, {})}
        >
          Re-Subscribe
        </Button>
      );
    }
    return (
      <>
        {isCurrentPlan ? (
          <Button
            color="red"
            h={45}
            disabled={isFreePlan || disabled}
            fw={500}
            onClick={() => setOpenedModalCancel(true)}
          >
            Cancel subscription
          </Button>
        ) : (
          <Button
            fw={500}
            h={45}
            disabled={isFreePlan || disabled || buttonLoading}
            loading={buttonLoading}
            onClick={() => onSubscribe?.(lookupKey || "", false, {})}
          >
            Subscribe
          </Button>
        )}
      </>
    );
  };

  const extraTextsComponent = extraTexts?.();

  return (
    <Card
      className="feature"
      shadow="md"
      withBorder
      padding="xl"
      radius="lg"
      flex={1}
      miw={320}
      maw={400}
      pb={extraTextsComponent ? 0 : "xl"}
    >
      {isCurrentPlan && <CurrentPlanComponent />}
      <Group gap="md" className="box-image-feature" justify="center">
        <Avatar
          src={mantaClassicLogo}
          size={100}
          radius={100}
          ml={50}
          mr={50}
          mt={50}
          mb={25}
        />
      </Group>
      <Group justify="center">
        <Title size="h2" fw={600}>
          {title}
        </Title>
      </Group>
      <Group justify="center">
        <Text
          size="sm"
          c={isFreePlan ? "grey" : COLORS.primary}
          fw={600}
          pb={20}
          mt={10}
        >
          {subtitle}
        </Text>
      </Group>
      <Group pb={10} mt={20}>
        <Title size="h3">{price}</Title>
        {priceSubtitle && (
          <Text size="sm" c="grey" fw={500} ml={-10}>
            {priceSubtitle}
          </Text>
        )}
      </Group>
      {customButton}
      {!customButton && renderDefaultButton()}
      <Group>
        <Text size="sm" fw={500} pt={10}>
          This includes:
        </Text>
      </Group>
      {features.map((feature, index) => (
        <Group key={index}>
          <Text size="sm" c="grey" fw={500} pt={10}>
            • {feature}
          </Text>
        </Group>
      ))}
      {extraTextsComponent && (
        <Box
          bg={"gray.1"}
          p={15}
          style={{
            marginLeft: -32,
            marginRight: -32,
            marginTop: 20,
            borderBottomLeftRadius: 15,
            borderBottomRightRadius: 15,
          }}
        >
          {extraTextsComponent}
        </Box>
      )}
    </Card>
  );
};

export default PlanCard;
